import React, { useState } from 'react';
import { LoanOfficer, FullReview } from '../../data/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { ProgressRing } from '../ui/ProgressRing';
import { ProgressBar } from '../ui/ProgressBar';
import { Badge } from '../ui/Badge';
import { useData, availableWeeks } from '../../context/DataContext';
import { Check, ChevronDown, ChevronRight, History, MessageSquare, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '../ui/Card';
interface LODetailsProps {
  lo: LoanOfficer;
  onComplete?: () => void;
}
export function LODetails({
  lo,
  onComplete
}: LODetailsProps) {
  const [activeTab, setActiveTab] = useState<keyof FullReview>('oneOnOne');
  const {
    selectedWeek,
    updateReviewField,
    updateReviewCategoryScore,
    toggleReviewCategoryCompletion,
    markReviewComplete
  } = useData();
  // Get the review data for the selected week, fallback to empty/first if not found
  const weekReview = lo.reviews[selectedWeek] || lo.reviews[Object.keys(lo.reviews)[0]];
  // Resolve the immediately prior week (availableWeeks is ordered newest-first,
  // so the previous week is the next entry after the selected one).
  const selectedWeekIndex = availableWeeks.findIndex(w => w.id === selectedWeek);
  const previousWeek = selectedWeekIndex >= 0 ? availableWeeks[selectedWeekIndex + 1] : undefined;
  const previousWeekReview = previousWeek ? lo.reviews[previousWeek.id] : undefined;
  const tabs: {
    key: keyof FullReview;
    label: string;
  }[] = [{
    key: 'oneOnOne',
    label: '1:1 Reviews'
  }, {
    key: 'gameFilms',
    label: 'Game Films'
  }, {
    key: 'eits',
    label: 'Eye In The Sky'
  }, {
    key: 'dailyHuddles',
    label: 'Daily Huddles'
  }, {
    key: 'trainings',
    label: 'Trainings'
  }, {
    key: 'production',
    label: 'Production'
  }, {
    key: 'pipeline',
    label: 'Pipeline'
  }, {
    key: 'shadowing',
    label: 'Shadowing'
  }, {
    key: 'callBlock',
    label: 'Call Blocks'
  }, {
    key: 'attendance',
    label: 'Attendance'
  }];
  const renderField = (label: string, fieldKey: string, value: string | number | boolean) => <div className="flex flex-col space-y-2" data-id="element-195">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-196">
        {label}
      </span>
      {typeof value === 'boolean' ? <button onClick={() => updateReviewField(lo.id, activeTab, fieldKey, !value)} className={cn('relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2', value ? 'bg-emerald-500' : 'bg-slate-200')} data-id="element-197">
          <span className={cn('pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out', value ? 'translate-x-5' : 'translate-x-0')} data-id="element-198" />
        </button> : typeof value === 'number' ? <input type="number" value={value} onChange={e => updateReviewField(lo.id, activeTab, fieldKey, Number(e.target.value))} className="w-full text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none" data-id="element-199" /> : <input type="text" value={value || ''} onChange={e => updateReviewField(lo.id, activeTab, fieldKey, e.target.value)} className="w-full text-sm border border-slate-200 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none" placeholder="-" data-id="element-200" />}
    </div>;
  const renderCategoryHeader = (category: any) => <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100" data-id="element-201">
      <div className="flex items-center space-x-6" data-id="element-202">
        <div className="flex items-center space-x-3" data-id="element-203">
          <span className="text-sm font-medium text-slate-700" data-id="element-204">Completed</span>
          <button onClick={() => toggleReviewCategoryCompletion(lo.id, activeTab)} className={cn('relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2', category.completed ? 'bg-emerald-500' : 'bg-slate-200')} data-id="element-205">
            <span className={cn('pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out', category.completed ? 'translate-x-5' : 'translate-x-0')} data-id="element-206" />
          </button>
        </div>
        <div className="flex items-center space-x-3" data-id="element-207">
          <span className="text-sm font-medium text-slate-700" data-id="element-208">Score</span>
          <div className="flex space-x-1" data-id="element-209">
            {[1, 2, 3, 4, 5].map(s => <button key={s} onClick={() => updateReviewCategoryScore(lo.id, activeTab, s)} className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors hover:bg-slate-800 hover:text-white', s <= category.score ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400')} data-id="element-210">
                {s}
              </button>)}
          </div>
        </div>
      </div>
      <div className="flex space-x-4" data-id="element-211">
        <div className="text-right" data-id="element-212">
          <span className="text-xs text-slate-500 block mb-1" data-id="element-213">
            Follow-up Date
          </span>
          <input type="date" value={category.followUpDate} onChange={e => updateReviewField(lo.id, activeTab, 'followUpDate', e.target.value)} className="text-sm font-medium text-slate-900 border border-slate-200 rounded-md px-2 py-1 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none" data-id="element-214" />
        </div>
      </div>
    </div>;
  const renderCategoryContent = () => {
    const data = weekReview[activeTab] as any;
    switch (activeTab) {
      case 'oneOnOne':
        return <div className="space-y-6" data-id="element-215">
            {renderCategoryHeader(data)}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-216">
              {renderField('Bi-Weekly Completed', 'biWeeklyCompleted', data.biWeeklyCompleted)}
              {renderField('Prev Month Perf', 'prevMonthPerfDiscussed', data.prevMonthPerfDiscussed)}
              {renderField('MTD Perf', 'currentMTDPerfDiscussed', data.currentMTDPerfDiscussed)}
              {renderField('Comp Discussion', 'compDiscussion', data.compDiscussion)}
              {renderField('Goal Review', 'goalReview', data.goalReview)}
              {renderField('Motivation/Why', 'motivationDiscussed', data.motivationDiscussed)}
              {renderField('Feedback Provided', 'feedbackProvided', data.feedbackProvided)}
              {renderField('Feedback Received', 'feedbackReceived', data.feedbackReceived)}
            </div>
            <div className="mt-6" data-id="element-217">
              {renderField('Open Discussion Notes', 'openDiscussionNotes', data.openDiscussionNotes)}
            </div>
          </div>;
      case 'eits':
        return <div className="space-y-6" data-id="element-218">
            {renderCategoryHeader(data)}
            <div className="bg-slate-50 p-4 rounded-lg mb-6" data-id="element-219">
              <div className="flex justify-between items-end mb-2" data-id="element-220">
                <span className="text-sm font-semibold text-slate-700" data-id="element-221">
                  Observation Target (300 min/wk)
                </span>
                <span className="text-sm font-bold text-slate-900" data-id="element-222">
                  {data.minutesObserved} / 300 min
                </span>
              </div>
              <ProgressBar value={data.minutesObserved} max={300} colorClass="bg-indigo-600" data-id="element-223" />
            </div>
            <div className="grid grid-cols-2 gap-6" data-id="element-224">
              {renderField('Coaching Moments', 'coachingMoments', data.coachingMoments)}
              {renderField('Real-Time Feedback', 'realTimeFeedbackDelivered', data.realTimeFeedbackDelivered)}
              {renderField('Strengths Observed', 'strengthsObserved', data.strengthsObserved)}
              {renderField('Areas Needing Improvement', 'areasNeedingImprovement', data.areasNeedingImprovement)}
            </div>
          </div>;
      case 'production':
        return <div className="space-y-6" data-id="element-225">
            {renderCategoryHeader(data)}
            <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-226">
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center" data-id="element-227">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-228">
                  Applications
                </span>
                <span className="block text-2xl font-bold text-slate-900" data-id="element-229">
                  {lo.production.applications}
                </span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center" data-id="element-230">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-231">
                  Submissions
                </span>
                <span className="block text-2xl font-bold text-slate-900" data-id="element-232">
                  {lo.production.submissions}
                </span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center" data-id="element-233">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-234">
                  Closings
                </span>
                <span className="block text-2xl font-bold text-slate-900" data-id="element-235">
                  {lo.production.closings}
                </span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center" data-id="element-236">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-237">
                  Conversion
                </span>
                <span className="block text-2xl font-bold text-slate-900" data-id="element-238">
                  {lo.production.conversionRate}%
                </span>
              </div>
            </div>
            {renderField('Manager Comments', 'managerComments', data.managerComments)}
          </div>;
      // Fallback for other categories to keep it concise for this demo
      default:
        return <div className="space-y-6" data-id="element-239">
            {renderCategoryHeader(data)}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-240">
              {Object.keys(data).filter(k => !['completed', 'score', 'notes', 'actionItems', 'followUpDate'].includes(k)).map(key => <div key={key} data-id="element-241">
                    {renderField(key.replace(/([A-Z])/g, ' $1').trim(), key, data[key])}
                  </div>)}
            </div>
          </div>;
    }
  };
  const activeIndex = tabs.findIndex(t => t.key === activeTab);
  const isLastSection = activeIndex === tabs.length - 1;
  const handleCompleteSection = () => {
    // Lock in the manager's choices/notes by marking this section complete
    updateReviewField(lo.id, activeTab, 'completed', true);
    if (!isLastSection) {
      // Automatically advance to the next section
      setActiveTab(tabs[activeIndex + 1].key);
    } else {
      // Final section: stamp the entire LO review as complete
      markReviewComplete(lo.id);
      // Allow the host (e.g. the executive modal) to collapse/close
      onComplete?.();
    }
  };
  const trend = lo.accountabilityScore - lo.priorMonthScore;
  return <div className="mt-6 border-t border-slate-100 pt-6" data-id="element-242">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-243">
        {/* Left Sidebar: Scoring & Navigation */}
        <div className="lg:col-span-1 space-y-6" data-id="element-244">
          <Card className="bg-slate-50 border-none shadow-none" data-id="element-245">
            <CardContent className="p-6" data-id="element-246">
              <h4 className="text-sm font-semibold text-slate-900 mb-4" data-id="element-247">
                Accountability Score
              </h4>
              <div className="flex items-center justify-center mb-4" data-id="element-248">
                <ProgressRing value={lo.accountabilityScore} size={120} strokeWidth={10} colorClass={lo.accountabilityScore >= 80 ? 'text-emerald-500' : lo.accountabilityScore >= 60 ? 'text-amber-500' : 'text-rose-500'} data-id="element-249" />
              </div>
              <div className="flex items-center justify-between text-sm mb-2" data-id="element-250">
                <span className="text-slate-500" data-id="element-251">Trend</span>
                <div className={cn('flex items-center font-medium', trend >= 0 ? 'text-emerald-600' : 'text-rose-600')} data-id="element-252">
                  {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" data-id="element-253" /> : <TrendingDown className="w-4 h-4 mr-1" data-id="element-254" />}
                  {Math.abs(trend)}%
                </div>
              </div>
              <div className="flex items-center justify-between text-sm" data-id="element-255">
                <span className="text-slate-500" data-id="element-256">Health</span>
                <Badge variant={lo.healthIndicator === 'Healthy' ? 'success' : lo.healthIndicator === 'At Risk' ? 'warning' : 'danger'} data-id="element-257">
                  {lo.healthIndicator}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col space-y-1" data-id="element-258">
            {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={cn('flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded-lg transition-colors', activeTab === tab.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')} data-id="element-259">
                {tab.label}
                {(weekReview[tab.key] as any).completed && <span className="flex items-center space-x-1.5" data-id="element-260">
                    <Check className={cn('w-4 h-4', activeTab === tab.key ? 'text-slate-300' : 'text-emerald-500')} data-id="element-261" />
                    <span className={cn('inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold', (weekReview[tab.key] as any).score >= 4 ? 'bg-emerald-100 text-emerald-700' : (weekReview[tab.key] as any).score >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700')} aria-label={`Score ${(weekReview[tab.key] as any).score} of 5`} data-id="element-262">
                      {(weekReview[tab.key] as any).score}
                    </span>
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Right Content Area: Review Form */}
        <div className="lg:col-span-3" data-id="element-263">
          <Card className="h-full border-slate-200" data-id="element-264">
            <CardHeader className="border-b border-slate-100 bg-white pb-4" data-id="element-265">
              <CardTitle className="text-lg flex items-center" data-id="element-266">
                {tabs.find(t => t.key === activeTab)?.label} Review
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6" data-id="element-267">
              {renderCategoryContent()}

              {/* Last week's notes & action items for easy reference */}
              {(() => {
              const prevCategory = previousWeekReview ? previousWeekReview[activeTab] as any : null;
              const prevNotes = prevCategory?.notes?.trim();
              const prevActions = prevCategory?.actionItems?.trim();
              const hasPrev = Boolean(prevNotes || prevActions);
              return <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50/60 overflow-hidden" data-id="element-268">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-amber-200/70 bg-amber-50" data-id="element-269">
                      <div className="flex items-center text-amber-800" data-id="element-270">
                        <History className="w-4 h-4 mr-2" data-id="element-271" />
                        <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-272">
                          Last Week's Reference
                        </span>
                      </div>
                      <span className="text-xs font-medium text-amber-700" data-id="element-273">
                        {previousWeek ? previousWeek.label : 'No prior week'}
                      </span>
                    </div>
                    {hasPrev ? <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-amber-200/70" data-id="element-274">
                        <div className="p-4" data-id="element-275">
                          <div className="flex items-center text-[11px] font-semibold uppercase tracking-wider text-amber-700 mb-1.5" data-id="element-276">
                            <MessageSquare className="w-3.5 h-3.5 mr-1.5" data-id="element-277" />
                            Notes
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-line" data-id="element-278">
                            {prevNotes || <span className="text-slate-400 italic" data-id="element-279">
                                No notes recorded
                              </span>}
                          </p>
                        </div>
                        <div className="p-4" data-id="element-280">
                          <div className="flex items-center text-[11px] font-semibold uppercase tracking-wider text-amber-700 mb-1.5" data-id="element-281">
                            <Target className="w-3.5 h-3.5 mr-1.5" data-id="element-282" />
                            Action Items
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-line" data-id="element-283">
                            {prevActions || <span className="text-slate-400 italic" data-id="element-284">
                                No action items recorded
                              </span>}
                          </p>
                        </div>
                      </div> : <div className="p-4 text-sm text-amber-700/80" data-id="element-285">
                        {previousWeek ? 'No notes or action items were recorded for this category last week.' : 'This is the earliest week available — no prior reference yet.'}
                      </div>}
                  </div>;
            })()}

              <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-286">
                <div data-id="element-287">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-288">
                    Manager Notes
                  </label>
                  <textarea className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[100px] focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none" value={(weekReview[activeTab] as any).notes} onChange={e => updateReviewField(lo.id, activeTab, 'notes', e.target.value)} placeholder="Add notes for this section..." data-id="element-289" />
                </div>
                <div data-id="element-290">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-291">
                    Action Items
                  </label>
                  <textarea className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[100px] focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none" value={(weekReview[activeTab] as any).actionItems} onChange={e => updateReviewField(lo.id, activeTab, 'actionItems', e.target.value)} placeholder="Define action items..." data-id="element-292" />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-293">
                <button className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50" data-id="element-294">
                  Save Draft
                </button>
                <button onClick={handleCompleteSection} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800" data-id="element-295">
                  {isLastSection ? 'Complete Review' : 'Complete & Next'}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}